import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ZigZagHeadingAndAltCtaInitModel,
  ZigZagHeadingAndAltCtaInitVariables,
  ZigZagHeadingAndAltCtaModel,
  ZigZagHeadingAndAltCtaVariables,
} from '../../../../models/email-template/zigzag-heading-and-alt-cta.interface';

/**
 * Mockup-styled ZigZag Heading + Secondary CTA block - a drop-in replacement
 * for app-zigzag-heading-and-alt-cta in block-renderer-v2 (same
 * [block]/[isPreview] inputs and outputs), so it covers both the
 * center-canvas summary (isPreview=true) and the right-panel edit
 * form (isPreview=false). Reuses the real, unmodified app-block-text (image
 * upload/segment/validation) and app-field-editor-v2/app-pin sub-components
 * and the exact same ZigZagHeadingAndAltCtaVariables field names - only the
 * surrounding layout is new. Same fixed image-left layout as the Primary CTA
 * variant (see zigzag-heading-and-cta-v2) - the only real difference is the
 * CTA itself, an underlined text link (left-aligned) instead of a filled
 * button, matching the mockup's own .cr-zigzag-alternate-cta styling.
 */
@Component({
  selector: 'app-zigzag-heading-and-alt-cta-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './zigzag-heading-and-alt-cta-v2.component.html',
  styleUrl: './zigzag-heading-and-alt-cta-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ZigZagHeadingAndAltCtaComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ZigZagHeadingAndAltCtaModel = new ZigZagHeadingAndAltCtaInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ZigZagHeadingAndAltCtaInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  headingPlaceholder = 'Enter Heading';
  textPlaceholder = 'Enter Text';
  ctaLinkPlaceholder = 'Enter CTA Link';
  ctaTextPlaceholder = 'Enter CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ZigZagHaacImageHref']);
    const segment = this.block.variables?.['ZigZagHaacImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get ctaPreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ZigZagHaacCtaLink']);
    const segment = this.block.variables?.['ZigZagHaacCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ZigZagHeadingAndAltCtaVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
